import React, { useState } from 'react';
import { Play, RotateCcw, Shuffle } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import { useScreensaver, SCREENSAVER_BACKGROUNDS } from '../../../context/ScreensaverContext';
import { THEME_REGISTRY, THEME_CATEGORIES, SHUFFLE_KEY } from '../../common/screensaver/themes';
import { CLOCK_REGISTRY } from '../../common/screensaver/clocks';

const BACKGROUND_PRESETS = [
  { key: 'auto', label: 'Match theme' },
  { key: 'solid_dark', label: 'Solid dark' },
  { key: 'solid_light', label: 'Solid light' },
  { key: 'gradient_sunset', label: 'Sunset' },
  { key: 'gradient_aurora', label: 'Aurora' },
  { key: 'gradient_ocean', label: 'Ocean' },
  { key: 'gradient_dawn', label: 'Dawn' },
  { key: 'gradient_compliance', label: 'Compliance blue' },
];

const COLOR_MODES = [
  { key: 'auto', label: 'Auto', hint: 'Uses your accent color' },
  { key: 'vibrant', label: 'Vibrant', hint: 'Warm, energetic palette' },
  { key: 'cycling', label: 'Cycling', hint: 'Rainbow — always moving' },
];

const ACCENT_SWATCHES = ['#3b82f6', '#2563eb', '#8b5cf6', '#ec4899', '#ef4444', '#f59e0b', '#10b981', '#14b8a6', '#0ea5e9'];

function Toggle({ on, onChange }) {
    return (
        <button
            onClick={onChange}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                on ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-600'
            }`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`}
            />
        </button>
    );
}

function ThemePreview({ themeKey, selected, onClick }) {
    const preview = PREVIEW_STYLES[themeKey] || PREVIEW_STYLES.particles;
    const meta = THEME_REGISTRY[themeKey];
    return (
        <button
            type="button"
            onClick={onClick}
            className={`group relative rounded-lg overflow-hidden border-2 transition-all ${
                selected
                    ? 'border-blue-500 ring-2 ring-blue-500/30'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
        >
            <div className="h-20 w-full" style={preview} />
            {meta.threeD && (
                <span className="absolute top-1 right-1 px-1 py-px text-[9px] font-semibold rounded bg-black/50 text-white tracking-wide">3D</span>
            )}
            <div className="px-2 py-1.5 bg-white dark:bg-gray-800">
                <div className="text-xs font-medium text-gray-900 dark:text-gray-100 text-left truncate">{meta.label}</div>
            </div>
        </button>
    );
}

// Lightweight static preview swatches — gives users a sense of each theme without running the animation.
const PREVIEW_STYLES = {
    particles: { background: 'radial-gradient(circle at 30% 40%, #3b82f6 0%, transparent 40%), radial-gradient(circle at 70% 60%, #8b5cf6 0%, transparent 40%), #0f172a' },
    rain: { background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)' },
    aurora: { background: 'linear-gradient(135deg, #064e3b 0%, #0f766e 50%, #1e3a8a 100%)' },
    starfield: { background: 'radial-gradient(circle at 50% 50%, #1e293b 0%, #000 100%)' },
    blobs: { background: 'radial-gradient(circle at 20% 30%, #ec4899 0%, transparent 35%), radial-gradient(circle at 80% 70%, #3b82f6 0%, transparent 35%), #0f172a' },
    matrix: { background: 'linear-gradient(180deg, #000 0%, #052e16 100%)' },
    compliance: { background: 'linear-gradient(180deg, #1e1b4b 0%, #1e3a8a 60%, #0369a1 100%)' },
    blueprint: { background: 'linear-gradient(180deg, #0b3a5e 0%, #1e40af 100%)' },
    fireflies: { background: 'radial-gradient(circle at 30% 70%, #fde68a 0%, transparent 25%), radial-gradient(circle at 75% 40%, #fbbf24 0%, transparent 20%), linear-gradient(180deg, #0a0f1e 0%, #331a4a 100%)' },
    bokeh: { background: 'radial-gradient(circle at 25% 35%, #ec489988 0%, transparent 30%), radial-gradient(circle at 75% 65%, #6366f188 0%, transparent 35%), linear-gradient(135deg, #1b0a2f 0%, #07172b 100%)' },
    synthwave: { background: 'radial-gradient(ellipse at 50% 60%, #fb923c 0%, #ec4899 30%, transparent 50%), linear-gradient(180deg, #2b1066 0%, #b21f6c 58%, #0b0320 60%)' },
    waves: { background: 'linear-gradient(180deg, #030712 0%, #0c1d3e 40%, #1e1b4b 70%, #3b82f655 100%)' },
    minimal: { background: 'radial-gradient(ellipse at center, #3b82f630 0%, #111827 70%)' },
    clouds: { background: 'radial-gradient(ellipse at 30% 45%, #e2e8f0cc 0%, transparent 45%), radial-gradient(ellipse at 70% 60%, #cbd5e1aa 0%, transparent 40%), linear-gradient(180deg, #93c5fd 0%, #2563eb 100%)' },
    fog: { background: 'radial-gradient(ellipse at 40% 70%, #94a3b888 0%, transparent 50%), radial-gradient(ellipse at 70% 40%, #64748b66 0%, transparent 45%), linear-gradient(180deg, #1e293b 0%, #0f172a 100%)' },
    galaxy: { background: 'radial-gradient(ellipse at 50% 50%, #c4b5fd 0%, #7c3aed 25%, #1e1b4b 60%, #020617 100%)' },
    earth: { background: 'radial-gradient(circle at 50% 55%, #1d4ed8 0%, #172554 45%, #020617 70%)' },
    solarSystem: { background: 'radial-gradient(circle at 50% 50%, #fbbf24 0%, #b45309 12%, transparent 30%), radial-gradient(circle at 78% 35%, #60a5fa 0%, transparent 6%), #020617' },
};

function AppearanceSettings() {
    const { theme, toggleTheme } = useTheme();
    const { currentUser } = useAuth();
    const { settings, updateSetting, resetSettings, manualTrigger } = useScreensaver();
    const [kprosHidden, setKprosHidden] = useState(() => localStorage.getItem('kpros_hidden') === 'true');
    const [kprosPosition, setKprosPosition] = useState(() => localStorage.getItem('kpros_position') || 'right');
    const [teamChatHidden, setTeamChatHidden] = useState(() => localStorage.getItem('team_chat_hidden') === 'true');

    const hasKpros = currentUser?.allowedFeatures?.includes('kpros');
    const hasTeamChat = currentUser?.allowedFeatures?.includes('team_chat');

    const toggleKprosVisibility = () => {
        const newValue = !kprosHidden;
        setKprosHidden(newValue);
        localStorage.setItem('kpros_hidden', String(newValue));
        window.dispatchEvent(new Event('kpros-visibility-changed'));
    };

    const changeKprosPosition = (pos) => {
        setKprosPosition(pos);
        localStorage.setItem('kpros_position', pos);
        window.dispatchEvent(new Event('kpros-visibility-changed'));
    };

    const toggleTeamChatVisibility = () => {
        const newValue = !teamChatHidden;
        setTeamChatHidden(newValue);
        localStorage.setItem('team_chat_hidden', String(newValue));
        window.dispatchEvent(new Event('team-chat-visibility-changed'));
    };

    const timeoutMinutes = (settings.timeoutMs / 60000).toFixed(1).replace(/\.0$/, '');

    return (
        <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-dm-serif font-semibold mb-4">Appearance</h2>

                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="font-unna font-medium">Dark Mode</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 font-rasa">
                                Toggle between dark and light theme
                            </p>
                        </div>
                        <Toggle on={theme === 'dark'} onChange={toggleTheme} />
                    </div>

                    {hasKpros && (
                        <>
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="font-unna font-medium">Hide Kpros Helper</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 font-rasa">
                                        Hide the floating Kpros button from the dashboard
                                    </p>
                                </div>
                                <Toggle on={kprosHidden} onChange={toggleKprosVisibility} />
                            </div>

                            {!kprosHidden && (
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-unna font-medium">Kpros Position</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 font-rasa">
                                            Choose which side the Kpros button appears on
                                        </p>
                                    </div>
                                    <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                        <button
                                            onClick={() => changeKprosPosition('left')}
                                            className={`px-3 py-1.5 text-sm font-rasa transition-colors ${
                                                kprosPosition === 'left'
                                                    ? 'bg-blue-500 text-white'
                                                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            Left
                                        </button>
                                        <button
                                            onClick={() => changeKprosPosition('right')}
                                            className={`px-3 py-1.5 text-sm font-rasa transition-colors ${
                                                kprosPosition === 'right'
                                                    ? 'bg-blue-500 text-white'
                                                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            Right
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {hasTeamChat && (
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-unna font-medium">Hide Team Chat Button</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 font-rasa">
                                    Hide the floating Team Chat button in the chat view
                                </p>
                            </div>
                            <Toggle on={teamChatHidden} onChange={toggleTeamChatVisibility} />
                        </div>
                    )}
                </div>
            </div>

            {/* Screensaver section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-1">
                    <h2 className="text-xl font-dm-serif font-semibold">Screensaver</h2>
                    <div className="flex gap-2">
                        <button
                            onClick={manualTrigger}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-rasa rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                        >
                            <Play size={14} />
                            Preview
                        </button>
                        <button
                            onClick={resetSettings}
                            title="Reset to defaults"
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-rasa rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            <RotateCcw size={14} />
                            Reset
                        </button>
                    </div>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-rasa mb-5">
                    Customize the idle screensaver. Keyboard shortcut to activate: <kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs font-mono">Ctrl/Cmd + Shift + S</kbd>
                </p>

                <div className="space-y-6">
                    {/* Animation theme */}
                    <div>
                        <h3 className="font-unna font-medium mb-2">Animation</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-3">
                            <button
                                type="button"
                                onClick={() => updateSetting('theme', SHUFFLE_KEY)}
                                className={`group relative rounded-lg overflow-hidden border-2 transition-all ${
                                    settings.theme === SHUFFLE_KEY
                                        ? 'border-blue-500 ring-2 ring-blue-500/30'
                                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                }`}
                            >
                                <div
                                    className="h-20 w-full flex items-center justify-center"
                                    style={{ background: 'conic-gradient(from 220deg at 50% 50%, #3b82f6, #fb923c, #10b981, #ec4899, #3b82f6)' }}
                                >
                                    <Shuffle size={22} className="text-white drop-shadow" />
                                </div>
                                <div className="px-2 py-1.5 bg-white dark:bg-gray-800">
                                    <div className="text-xs font-medium text-gray-900 dark:text-gray-100 text-left truncate">Surprise Me</div>
                                </div>
                            </button>
                        </div>
                        {THEME_CATEGORIES.map(category => (
                            <div key={category} className="mb-3">
                                <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 font-rasa mb-1.5">{category}</div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                    {Object.keys(THEME_REGISTRY).filter(key => THEME_REGISTRY[key].category === category).map(key => (
                                        <ThemePreview
                                            key={key}
                                            themeKey={key}
                                            selected={settings.theme === key}
                                            onClick={() => updateSetting('theme', key)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-rasa mt-2">
                            {settings.theme === SHUFFLE_KEY
                                ? 'A different theme every time the screensaver starts'
                                : THEME_REGISTRY[settings.theme]?.description}
                        </p>
                    </div>

                    {/* Clock style */}
                    <div>
                        <h3 className="font-unna font-medium mb-2">Clock style</h3>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(CLOCK_REGISTRY).map(([key, meta]) => (
                                <button
                                    key={key}
                                    onClick={() => updateSetting('clockStyle', key)}
                                    className={`px-3 py-1.5 text-sm font-rasa rounded-lg border transition-colors ${
                                        settings.clockStyle === key
                                            ? 'bg-blue-500 text-white border-blue-500'
                                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    {meta.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Color mode */}
                    <div>
                        <h3 className="font-unna font-medium mb-2">Colors</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                            {COLOR_MODES.map(mode => (
                                <button
                                    key={mode.key}
                                    onClick={() => updateSetting('colorMode', mode.key)}
                                    className={`text-left px-3 py-2 rounded-lg border transition-colors ${
                                        settings.colorMode === mode.key
                                            ? 'bg-blue-50 dark:bg-blue-500/10 border-blue-500'
                                            : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    <div className="font-rasa text-sm font-medium text-gray-900 dark:text-gray-100">{mode.label}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 font-rasa">{mode.hint}</div>
                                </button>
                            ))}
                        </div>

                        {settings.colorMode === 'auto' && (
                            <div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 font-rasa mb-1.5">Accent color</div>
                                <div className="flex flex-wrap gap-2">
                                    {ACCENT_SWATCHES.map(color => (
                                        <button
                                            key={color}
                                            onClick={() => updateSetting('accentColor', color)}
                                            className={`w-7 h-7 rounded-full transition-transform ${settings.accentColor === color ? 'ring-2 ring-offset-2 ring-blue-500 dark:ring-offset-gray-800 scale-110' : 'hover:scale-110'}`}
                                            style={{ background: color }}
                                            aria-label={`Accent ${color}`}
                                        />
                                    ))}
                                    <label className="flex items-center justify-center w-7 h-7 rounded-full border-2 border-dashed border-gray-300 dark:border-gray-600 cursor-pointer hover:border-blue-500 transition-colors overflow-hidden">
                                        <input
                                            type="color"
                                            value={settings.accentColor}
                                            onChange={(e) => updateSetting('accentColor', e.target.value)}
                                            className="w-8 h-8 cursor-pointer opacity-0"
                                        />
                                        <span className="absolute text-[10px] text-gray-400">+</span>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Background */}
                    <div>
                        <h3 className="font-unna font-medium mb-2">Background</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {BACKGROUND_PRESETS.map(bg => {
                                const style = bg.key === 'auto'
                                    ? { background: theme === 'dark' ? '#030712' : '#f9fafb' }
                                    : SCREENSAVER_BACKGROUNDS[bg.key];
                                return (
                                    <button
                                        key={bg.key}
                                        onClick={() => updateSetting('background', bg.key)}
                                        className={`group relative rounded-lg overflow-hidden border-2 transition-all ${
                                            settings.background === bg.key
                                                ? 'border-blue-500 ring-2 ring-blue-500/30'
                                                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                        }`}
                                    >
                                        <div className="h-14 w-full" style={style} />
                                        <div className="px-2 py-1 bg-white dark:bg-gray-800 text-xs text-gray-800 dark:text-gray-200 text-left truncate">
                                            {bg.label}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Timeout slider */}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <h3 className="font-unna font-medium">Idle timeout</h3>
                            <span className="text-sm font-rasa text-gray-600 dark:text-gray-300">{timeoutMinutes} min</span>
                        </div>
                        <input
                            type="range"
                            min="30000"
                            max="900000"
                            step="30000"
                            value={settings.timeoutMs}
                            onChange={(e) => updateSetting('timeoutMs', Number(e.target.value))}
                            className="w-full accent-blue-500"
                        />
                        <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 font-rasa mt-1">
                            <span>30s</span>
                            <span>15 min</span>
                        </div>
                    </div>

                    {/* Toggles */}
                    <div className="space-y-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-unna font-medium">Show weather</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 font-rasa">
                                    Display local temperature and city (uses browser geolocation)
                                </p>
                            </div>
                            <Toggle on={settings.showWeather} onChange={() => updateSetting('showWeather', !settings.showWeather)} />
                        </div>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-unna font-medium">Show date</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 font-rasa">
                                    Full date under the clock
                                </p>
                            </div>
                            <Toggle on={settings.showDate} onChange={() => updateSetting('showDate', !settings.showDate)} />
                        </div>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-unna font-medium">Show seconds</h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400 font-rasa">
                                    Ticking seconds indicator
                                </p>
                            </div>
                            <Toggle on={settings.showSeconds} onChange={() => updateSetting('showSeconds', !settings.showSeconds)} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AppearanceSettings;
